import { observer } from "mobx-react-lite";
import { type FC } from "react";
import { Link } from "react-router-dom";

import type { ITransactions } from "@/api/restaurant/types/transactionsTypes";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  TableHeadCellSort,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import { rootStore } from "@/store/RootStore";
import {
  type PaginatedResponse,
  PartialState,
  STATUS_MAP_IS_PARTIAL_TRANSACTIONS,
} from "@/types/api";
import { createSortHandler } from "@/utils/createSortHandler";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { getIdsForFilter } from "@/utils/getIdsForFilter";
import { useAdminMoney } from "@/utils/hooks/useAdminMoney";
import { type IGroupParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import type { ColumnConfig } from "../type";

enum TransactionsColumn {
  DATE_TIME = "Date and time",
  ORDER_CODE = "Order code",
  BILL_NUMBER = "Bill number",
  TABLE_NUMBER = "Table number",
  BRAND = "Brand",
  BRANCH = "Branch",
  GROSS_AMOUNT = "Gross amount",
  TAX_AMOUNT = "Taxes",
  TIPS = "Tips",
  NET_AMOUNT = "Net amount",
  PAYABLE_AMOUNT = "Payable amount",
  SETTLEMENT_STATUS = "Payment status",
}

interface TransactionsTableProps {
  tableData: PaginatedResponse<ITransactions>;
  path?: (id: string) => string;
  tableParams: IGroupParams;
  setTableParams: React.Dispatch<React.SetStateAction<IGroupParams>>;
  handleNext: () => void;
  handlePrev: () => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
  isPending: boolean;
  isTableNumber?: boolean;
}

const TransactionsTable: FC<TransactionsTableProps> = observer(
  ({
    tableData,
    path,
    tableParams,
    setTableParams,
    handleNext,
    handlePrev,
    searchValue,
    setSearchValue,
    isPending,
    isTableNumber,
  }) => {
    const {
      availableBrands,
      availableBranches,
      hasNoBranchesForSelectedBrand,
      selectedBrand,
      selectedBranch,
    } = rootStore.restaurantAccessStore;

    const brandFilterOptions = availableBrands.map(b => ({
      id: b.id,
      name: b.name,
    }));
    const branchFilterOptions = availableBranches.map(b => ({
      id: b.id,
      name: b.name,
    }));

    const hasBrandColumn = tableData?.items?.length
      ? tableData.items.some(item => item?.brandName)
      : true;

    const hasBranchColumn = tableData?.items?.length
      ? tableData.items.some(item => item?.branchName)
      : true;

    const showBrandFilter =
      !selectedBrand && !selectedBranch && availableBrands.length > 1;

    const showBranchFilter =
      !selectedBranch &&
      !hasNoBranchesForSelectedBrand &&
      availableBranches.length > 1;

    const TABLE_HEADERS: TableHeaderType = Object.values(
      TransactionsColumn
    ).filter(header => {
      if (header === TransactionsColumn.BRAND) {
        return hasBrandColumn;
      }

      if (header === TransactionsColumn.BRANCH) {
        return hasBranchColumn;
      }

      return true;
    });

    const SUB_MENUS = {
      ...(showBrandFilter && {
        [TransactionsColumn.BRAND]: brandFilterOptions,
      }),
      ...(showBranchFilter && {
        [TransactionsColumn.BRANCH]: branchFilterOptions,
      }),
      [TransactionsColumn.GROSS_AMOUNT]: [],
      Status: [PartialState.full, PartialState.partial],
    };

    const MENU_LIST = Object.keys(SUB_MENUS);

    const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
      ...(showBrandFilter && {
        [TransactionsColumn.BRAND]: {
          type: "search",
          placeholder: "Search brand",
        },
      }),
      ...(showBranchFilter && {
        [TransactionsColumn.BRANCH]: {
          type: "search",
          placeholder: "Search branch",
        },
      }),
      [TransactionsColumn.GROSS_AMOUNT]: {
        type: "amount",
        range: true,
      },
      ["Status"]: { type: "status" },
    };

    const handleSetFilter = (selectedMap: Record<string, string[]>) => {
      const rawAmount = selectedMap[TransactionsColumn.GROSS_AMOUNT]?.[0];

      let amountFrom: string | undefined;
      let amountTo: string | undefined;

      if (rawAmount?.includes("-")) {
        const [from, to] = rawAmount.split("-");
        amountFrom = from || undefined;
        amountTo = to || undefined;
      } else {
        amountFrom = rawAmount || undefined;
      }

      const selectedBrands = getIdsForFilter(
        selectedMap[TransactionsColumn.BRAND]
      );
      const selectedBranches = getIdsForFilter(
        selectedMap[TransactionsColumn.BRANCH]
      );

      const selectedStatuses = selectedMap["Status"] || [];

      let isPartial: boolean | undefined = undefined;
      if (selectedStatuses.length === 1) {
        isPartial = selectedStatuses[0] === PartialState.partial;
      }

      setTableParams(prev => ({
        ...prev,
        amountFrom,
        amountTo,
        brandIds: selectedBrands.length > 0 ? selectedBrands : undefined,
        branchIds: selectedBranches.length > 0 ? selectedBranches : undefined,
        isPartial,
        offset: 0,
      }));
    };

    const handleSortDate = createSortHandler(setTableParams, "createdAt");
    const handleSortNetAmount = createSortHandler(setTableParams, "netAmount");
    const handleSortTipsAmount = createSortHandler(
      setTableParams,
      "tipsAmount"
    );
    const handleSortGrossAmount = createSortHandler(
      setTableParams,
      "totalAmount"
    );
    const handleSortTaxsAmount = createSortHandler(setTableParams, "taxAmount");

    const { visibleColumns, setVisibleColumns, flags } =
      useVisibleFlags(TABLE_HEADERS);

    const brandColumn: ColumnConfig<ITransactions> | null = hasBrandColumn
      ? {
          key: "brandName",
          label: TransactionsColumn.BRAND,
          visible: flags[TransactionsColumn.BRAND],
          headerComponent: ({ label }) => (
            <TableHeadCellCustom>{label}</TableHeadCellCustom>
          ),
          cellRenderer: (row, isLast) => (
            <TableCellText last={isLast}>{row?.brandName || "-"}</TableCellText>
          ),
        }
      : null;

    const branchColumn: ColumnConfig<ITransactions> | null = hasBranchColumn
      ? {
          key: "branchName",
          label: TransactionsColumn.BRANCH,
          visible: flags[TransactionsColumn.BRANCH],
          headerComponent: ({ label }) => (
            <TableHeadCellCustom>{label}</TableHeadCellCustom>
          ),
          cellRenderer: (row, isLast) => (
            <TableCellText last={isLast}>
              {row?.branchName || "-"}
            </TableCellText>
          ),
        }
      : null;

    const tableNumberColumn: ColumnConfig<ITransactions> | null = isTableNumber
      ? {
          key: "tableNumber",
          label: TransactionsColumn.TABLE_NUMBER,
          visible: flags[TransactionsColumn.TABLE_NUMBER],
          headerComponent: ({ label }) => (
            <TableHeadCellCustom>{label}</TableHeadCellCustom>
          ),
          cellRenderer: (row, isLast) => (
            <TableCellText last={isLast}>
              {row?.tableNumber ?? "-"}
            </TableCellText>
          ),
        }
      : null;

    const money = useAdminMoney();

    const columns: ColumnConfig<ITransactions>[] = [
      {
        key: "createdAt",
        label: TransactionsColumn.DATE_TIME,
        visible: flags[TransactionsColumn.DATE_TIME],
        headerComponent: ({ label }) => (
          <TableHeadCellSort
            sortOrder={tableParams.sortDirection}
            onSortChange={handleSortDate}
            field="createdAt"
            activeField={tableParams.sortBy}
          >
            {label}
          </TableHeadCellSort>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>
            {formatIsoDate(row.createdAt)}
          </TableCellText>
        ),
      },
      {
        key: "orderCode",
        label: TransactionsColumn.ORDER_CODE,
        visible: flags[TransactionsColumn.ORDER_CODE],
        headerComponent: ({ label }) => (
          <TableHeadCellCustom>{label}</TableHeadCellCustom>
        ),
        cellRenderer: (row, isLast) =>
          path ? (
            <TableCellLink last={isLast}>
              <Link to={path(row.id)} data-testid={`cell-id-${row.id}`}>
                {row.orderCode}
              </Link>
            </TableCellLink>
          ) : (
            <TableCellText last={isLast}>{row.orderCode}</TableCellText>
          ),
      },
      {
        key: "billNumber",
        label: TransactionsColumn.BILL_NUMBER,
        visible: flags[TransactionsColumn.BILL_NUMBER],
        headerComponent: ({ label }) => (
          <TableHeadCellCustom>{label}</TableHeadCellCustom>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>{row?.billNumber ?? "-"}</TableCellText>
        ),
      },

      ...(brandColumn ? [brandColumn] : []),
      ...(branchColumn ? [branchColumn] : []),
      ...(tableNumberColumn ? [tableNumberColumn] : []),
      {
        key: "grossAmount",
        label: TransactionsColumn.GROSS_AMOUNT,
        visible: flags[TransactionsColumn.GROSS_AMOUNT],
        headerComponent: ({ label }) => (
          <TableHeadCellSort
            sortOrder={tableParams.sortDirection}
            onSortChange={handleSortGrossAmount}
            field="totalAmount"
            activeField={tableParams.sortBy}
          >
            {label}
          </TableHeadCellSort>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>
            {money.render(row.grossAmount)}
          </TableCellText>
        ),
      },
      {
        key: "taxAmount",
        label: TransactionsColumn.TAX_AMOUNT,
        visible: flags[TransactionsColumn.TAX_AMOUNT],
        headerComponent: ({ label }) => (
          <TableHeadCellSort
            sortOrder={tableParams.sortDirection}
            onSortChange={handleSortTaxsAmount}
            field="taxAmount"
            activeField={tableParams.sortBy}
          >
            {label}
          </TableHeadCellSort>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>
            {money.render(row.taxAmount)}
          </TableCellText>
        ),
      },
      {
        key: "tipsAmount",
        label: TransactionsColumn.TIPS,
        visible: flags[TransactionsColumn.TIPS],
        headerComponent: ({ label }) => (
          <TableHeadCellSort
            sortOrder={tableParams.sortDirection}
            onSortChange={handleSortTipsAmount}
            field="tipsAmount"
            activeField={tableParams.sortBy}
          >
            {label}
          </TableHeadCellSort>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>
            {money.render(row.tipsAmount)}
          </TableCellText>
        ),
      },
      {
        key: "netAmount",
        label: TransactionsColumn.NET_AMOUNT,
        visible: flags[TransactionsColumn.NET_AMOUNT],
        headerComponent: ({ label }) => (
          <TableHeadCellSort
            sortOrder={tableParams.sortDirection}
            onSortChange={handleSortNetAmount}
            field="netAmount"
            activeField={tableParams.sortBy}
          >
            {label}
          </TableHeadCellSort>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>
            {money.render(row.netAmount)}
          </TableCellText>
        ),
      },
      {
        key: "totalPayable",
        label: TransactionsColumn.PAYABLE_AMOUNT,
        visible: flags[TransactionsColumn.PAYABLE_AMOUNT],
        headerComponent: ({ label }) => (
          <TableHeadCellCustom>{label}</TableHeadCellCustom>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellText last={isLast}>
            {money.render(row?.totalPayable)}
          </TableCellText>
        ),
      },
      {
        key: "status",
        label: TransactionsColumn.SETTLEMENT_STATUS,
        visible: flags[TransactionsColumn.SETTLEMENT_STATUS],
        headerComponent: ({ label }) => (
          <TableHeadCellCustom>{label}</TableHeadCellCustom>
        ),
        cellRenderer: (row, isLast) => (
          <TableCellBadge
            last={isLast}
            stage={
              STATUS_MAP_IS_PARTIAL_TRANSACTIONS[
                row.isPartial ? PartialState.partial : PartialState.full
              ]?.badge
            }
            classes="w-[11%]"
          >
            {row.isPartial ? PartialState.partial : PartialState.full}
          </TableCellBadge>
        ),
      },
    ];

    return (
      <>
        <FilterSearch
          menuList={MENU_LIST}
          subMenus={SUB_MENUS}
          subMenuRegistry={SUB_MENU_REGISTRY}
          statusVariant={STATUS_MAP_IS_PARTIAL_TRANSACTIONS}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          handleSetFilter={handleSetFilter}
        >
          <DropDownViewMenu
            viewList={TABLE_HEADERS}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
          />
        </FilterSearch>

        {isPending ? (
          <Loader />
        ) : (
          <DynamicTable
            data={tableData?.items || []}
            columns={columns}
            searchValue={searchValue}
            next={handleNext}
            prev={handlePrev}
            tableParams={tableData}
            setTableParams={setTableParams}
          />
        )}
      </>
    );
  }
);

export default TransactionsTable;
